import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import { inject, injectable, symbolToken } from '@servicetitan/react-ioc';

import { AUTH_STORE_TOKEN, IAuthStore } from '../auth';
import { NotificationCenterStore } from '../common';

export interface ApiGroupModel {
    displayName: string;
    apis: ApiModel[];
}

export interface ApiModel {
    id: string;
    displayName: string;
    description: string | null;
    isBeta: boolean;
    hasWebhooks: boolean;
}

export interface OpenApiDocument {
    info: {
        title: string;
        description?: string;
    };
    servers: {
        url: string;
        description?: string;
    }[];
    paths: {
        [path: string]: {
            [method: string]: OpenApiOperation;
        };
    };
    webhooks?: {
        [name: string]: {
            [method: string]: OpenApiOperation;
        };
    };
    components: {
        schemas: JsonSchemaCollection;
        securitySchemes: {
            [name: string]: OpenApiSecurityScheme;
        };
    };
}

export interface OpenApiOperation {
    tags: string[];
    summary?: string;
    description?: string;
    operationId: string;
    parameters: OpenApiParameter[];
    requestBody?: {
        content: {
            [mediaType: string]: OpenApiMediaType;
        };
    };
    responses: {
        [statusCode: string]: OpenApiResponse;
    };
    security?: OpenApiSecurityRequirement[];
}

export interface OpenApiParameter {
    name: string;
    in: 'header' | 'path' | 'query';
    description?: string;
    required?: boolean;
    schema: JsonSchema;
}

export interface OpenApiResponse {
    description?: string;
    content?: {
        [mediaType: string]: OpenApiMediaType;
    };
}

export interface OpenApiMediaType {
    schema: JsonSchemaOrRef;
    example: any;
}

export interface JsonSchemaCollection {
    [name: string]: JsonSchema;
}

export interface JsonSchemaArray {
    type: 'array' | ['array', 'null'];
    items: JsonSchemaOrRef;
    description?: string;
}

export interface JsonSchemaDiscriminator {
    propertyName: string;
    mapping: {
        [value: string]: string; // e.g. "cat" -> "#/components/schemas/Cat"
    };
}

export interface JsonSchemaObject {
    required?: string[];
    type: 'object';
    properties?: {
        [name: string]: JsonSchemaOrRef;
    };
    description?: string;
    discriminator?: JsonSchemaDiscriminator;
}

export type JsonSchemaPrimitiveType = 'string' | 'number' | 'integer' | 'boolean';

export interface JsonSchemaPrimitive {
    'enum'?: (number | string)[];
    'type'?: JsonSchemaPrimitiveType | [JsonSchemaPrimitiveType, 'null'];
    'description'?: string;
    'format'?: string;
    'x-enumNames'?: string[];
    'oneOf'?: JsonSchemaOrRef[];
}

export interface JsonSchemaUntyped {
    type: never;
    description?: string;
    allOf?: JsonSchemaOrRef[];
    anyOf?: JsonSchemaOrRef[];
    oneOf?: JsonSchemaOrRef[];
}

export type JsonSchema =
    | JsonSchemaArray
    | JsonSchemaObject
    | JsonSchemaPrimitive
    | JsonSchemaUntyped;

export type JsonSchemaOrRef = JsonSchema | { $ref: string };

export interface OpenApiSecurityRequirement {
    [name: string]: string[];
}

export interface OpenApiSecuritySchemeApiKey {
    type: 'apiKey';
    description?: string;
    name: string;
    in: 'header' | 'query' | 'cookie';
}

export interface OpenApiSecuritySchemeOAuth2 {
    type: 'oauth2';
    description?: string;
    flows: {
        clientCredentials: {
            tokenUrl: string;
            scopes: {
                [scope: string]: string;
            };
        };
    };
}

export type OpenApiSecurityScheme = OpenApiSecuritySchemeApiKey | OpenApiSecuritySchemeOAuth2;

export interface IDocumentationApi {
    getApis(): Promise<ApiGroupModel[]>;
    getApi(apiId: string): Promise<OpenApiDocument>;
}

export const DOCUMENTATION_API_TOKEN = symbolToken<IDocumentationApi>('DOCUMENTATION_API_TOKEN');

@injectable()
export class DocumentationApi implements IDocumentationApi {
    private readonly axios = axios.create();
    private getApisPromise: Promise<ApiGroupModel[]> | null = null;

    constructor(
        @inject(AUTH_STORE_TOKEN) private readonly authStore: IAuthStore,
        @inject(NotificationCenterStore)
        private readonly notificationCenterStore: NotificationCenterStore,
    ) {
        this.axios.interceptors.response.use(undefined, (error: AxiosError) => {
            this.notificationCenterStore.addToast({
                status: 'critical',
                title: 'Error',
                message: error.message,
            });
            return Promise.reject(error);
        });
    }

    async getApis(): Promise<ApiGroupModel[]> {
        if (!this.getApisPromise) {
            const axiosPromise =
                this.authStore.isAuthenticated === true
                    ? this.get<ApiGroupModel[]>('/api/private-docs/apis', { authorize: true })
                    : this.get<ApiGroupModel[]>('/api/docs/apis');
            this.getApisPromise = axiosPromise.then(response => response.data);
        }
        return this.getApisPromise;
    }

    async getApi(apiId: string): Promise<OpenApiDocument> {
        const response =
            this.authStore.isAuthenticated === true
                ? await this.get<OpenApiDocument>(`/api/private-docs/apis/${apiId}`, {
                      authorize: true,
                  })
                : await this.get<OpenApiDocument>(`/api/docs/apis/${apiId}`);
        return response.data;
    }

    private get<T>(url: string, options: { authorize?: boolean } = {}): Promise<AxiosResponse<T>> {
        return this.axios.get<T>(url, this.getConfig(options));
    }

    private getConfig({ authorize = false }: { authorize?: boolean } = {}): AxiosRequestConfig {
        return {
            headers: {
                Accept: 'application/json',
                Authorization:
                    authorize && this.authStore.accessToken
                        ? `Bearer ${this.authStore.accessToken}`
                        : undefined,
            },
        };
    }
}
